<template>
  <div class="container">
    <!-- 标题 -->
    <h1 class="title">🎉 Fun Puzzle 🧩</h1>

    <!-- 文件上传 -->
    <div class="upload-section">
      <label class="file-upload-btn">
        📷 点击上传图片（可多选）
        <input 
          type="file"
          multiple
          accept="image/*"
          @change="handleUpload"
          class="hidden-input"
        >
      </label>
    </div>

    <!-- 选择网格大小 -->
    <select v-model="gridSize">
      <option :value="2">非常简单 (2x2)</option>
      <option :value="3">简单 (3x3)</option>
      <option :value="4">中等 (4x4)</option>
      <option :value="5">困难 (5x5)</option>
    </select>

    <!-- 图片导航 -->
    <div v-if="images.length" class="nav-buttons">
      <button 
        @click="changeImage(-1)"
        :disabled="currentIndex === 0"
        class="nav-btn"
      >
        ◀ 上一张
      </button>
      <span class="counter">{{ currentIndex + 1 }}/{{ images.length }}</span>
      <button 
        @click="changeImage(1)"
        :disabled="currentIndex === images.length - 1"
        class="nav-btn"
      >
        下一张 ▶
      </button>
    </div>

    <!-- 拼图容器 -->
    <div 
      v-if="currentPuzzle.tiles.length > 0"
      class="puzzle-container"
      :style="{
        gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
        gridTemplateRows: `repeat(${gridSize}, 1fr)`
      }"
      @dragover.prevent
      @drop="handleDrop"
    >
      <div
        v-for="(tile, index) in currentPuzzle.tiles"
        :key="index"
        class="tile"
        :class="{
          correct: isTileCorrect(tile),
          selected: selectedIndex === index
        }"
        :draggable="true"
        @dragstart="handleDragStart(index)"
        @dragover.prevent
        :style="getTileStyle(tile)"
        @click="handleTileClick(index)"
      ></div>

    </div>

    <!-- 成功弹窗 -->
    <!-- 普通成功 Modal -->
    <div v-if="showSuccess" class="success-modal" @click.self="closeModal">
      <div class="modal-content">
        <span class="modal-close" @click="closeModal">×</span>
        <h2 class="celebrate-text">🎊 拼图成功！ 🎉</h2>
        <button @click="nextPuzzle" class="modal-btn">下一张挑战 ➡</button>
      </div>
    </div>

    <!-- 最终成功 Modal -->
    <div v-if="showFinalSuccess" class="final-success" @click.self="closeModal">
      <div class="modal-content">
        <span class="modal-close" @click="closeModal">×</span>
        <h2 class="celebrate-text">🏆 真棒！挑战成功啦！ 🏅</h2>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive , watch} from 'vue'

const images = ref([])
const currentIndex = ref(0)
const showSuccess = ref(false)
const showFinalSuccess = ref(false)
const dragStartIndex = ref(null)
const fileInput = ref(null)

const gridSize = ref(3)  // 默认网格大小为3x3

const currentPuzzle = reactive({
  original: [],
  tiles: [],
  positions: []
})
const selectedIndex = ref(null)

const handleTileClick = (index) => {
  if (selectedIndex.value === null) {
    selectedIndex.value = index
  } else {
    swapTiles(selectedIndex.value, index)
    selectedIndex.value = null
    checkCompletion()
  }
}

// 处理文件上传
const handleUpload = async (e) => {
  const files = Array.from(e.target.files)
  images.value = await Promise.all(files.map(convertToBase64))
  generatePuzzle()
}

const completedImages = ref([])

// 修改后的拼图生成逻辑
const generatePuzzle = () => {
  const img = new Image()
  img.src = images.value[currentIndex.value]
  img.onload = () => {
    const isCompleted = completedImages.value[currentIndex.value]
    const totalTiles = gridSize.value * gridSize.value // 根据 gridSize 计算总拼图块数

    const tiles = Array.from({ length: totalTiles }, (_, i) => ({
      correctPosition: i,
      background: generateTileBackground(img, i)
    }))

    currentPuzzle.tiles = isCompleted ? 
      [...tiles] : 
      shuffleArray(tiles).map((tile, index) => ({
        ...tile,
        position: index
      }))
  }
}

// 辅助打乱函数
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

// 生成拼图块背景
const generateTileBackground = (img, index) => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const size = Math.min(img.width, img.height)
  const tileSize = size / gridSize.value // 动态计算 tile 大小
  
  canvas.width = tileSize
  canvas.height = tileSize

  const col = index % gridSize.value
  const row = Math.floor(index / gridSize.value)
  
  ctx.drawImage(
    img,
    col * tileSize,
    row * tileSize,
    tileSize,
    tileSize,
    0,
    0,
    tileSize,
    tileSize
  )

  return `url(${canvas.toDataURL()})`
}



// 监听 gridSize 的变化
watch(gridSize, () => {
  generatePuzzle()
})


const checkCompletion = () => {
  const isComplete = currentPuzzle.tiles.every(
    (tile, index) => tile.correctPosition === index
  )
  
  if (isComplete) {
    completedImages.value[currentIndex.value] = true
    showSuccess.value = true
  }
}

const resetGame = () => {
  completedImages.value = []
  images.value = []
  currentIndex.value = 0
  showSuccess.value = false
  showFinalSuccess.value = false
  fileInput.value.value = ''
  currentPuzzle.tiles = []
}

// 增强的下一张方法
const nextPuzzle = () => {
  showSuccess.value = false
  if (currentIndex.value < images.value.length - 1) {
    currentIndex.value++
    generatePuzzle()
  } else {
    showFinalSuccess.value = true
  }
}

// 新增关闭弹窗方法
const closeModal = () => {
  showSuccess.value = false
  showFinalSuccess.value = false
}


// 拖动处理
const handleDragStart = (index) => {
  dragStartIndex.value = index
}

const handleDrop = (e) => {
  const dragEndIndex = [...e.target.parentNode.children].indexOf(e.target)
  swapTiles(dragStartIndex.value, dragEndIndex)
  checkCompletion()
}

// 交换拼图块
const swapTiles = (startIndex, endIndex) => {
  const tiles = currentPuzzle.tiles
  ;[tiles[startIndex], tiles[endIndex]] = [tiles[endIndex], tiles[startIndex]]
  currentPuzzle.tiles = [...tiles]
}

// 检查是否完成
const isTileCorrect = (tile) => {
  return tile.correctPosition === tile.position
}

const getTileStyle = (tile) => ({
  backgroundImage: tile.background,
  backgroundSize: 'cover'
})

const changeImage = (delta) => {
  currentIndex.value = Math.max(0, 
    Math.min(images.value.length - 1, currentIndex.value + delta))
  generatePuzzle()
}

const convertToBase64 = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
  })
}
</script>

<style scoped>
/* 你的现有样式保持不变 */

.grid-size-select {
  margin: 20px 0;
}
</style>


<style scoped>

.puzzle-container {
  display: grid;
  gap: 2px;
  width: 500px;
  height: 500px;
  margin: 20px auto;
  border: 2px solid #4ecdc4;
  border-radius: 10px;
  overflow: hidden;
}

.tile {
  aspect-ratio: 1;
  background: #fff;
  transition: transform 0.3s;
  cursor: grab;
  border: 1px solid rgba(0,0,0,0.1);
}
.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  text-align: center;
}

.title {
  font-size: 3rem;
  color: #ff6b6b;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
  margin-bottom: 2rem;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.puzzle-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  width: 500px;
  height: 500px;
  margin: 20px auto;
  border: 2px solid #4ecdc4;
  border-radius: 10px;
  overflow: hidden;
}

.tile {
  aspect-ratio: 1;
  background: #fff;
  transition: transform 0.3s;
  cursor: grab;
  border: 1px solid rgba(0,0,0,0.1);
}

.tile:hover {
  transform: scale(1.02);
  z-index: 1;
  box-shadow: 0 0 10px rgba(0,0,0,0.2);
}

.correct {
  border: 2px solid #4ecdc4;
}


.celebrate-text {
  animation: shake 0.8s ease infinite;
  color: #ff6b6b;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

@keyframes popIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

.vibrant-btn {
  background: #4ecdc4;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  margin: 10px;
}

.vibrant-btn:hover {
  background: #45b7b0;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
}

.nav-buttons {
  margin: 20px 0;
}

.nav-btn {
  background: #ff6b6b;
  color: white;
  margin: 0 10px;
  padding: 8px 16px;
  border-radius: 20px;
  transition: all 0.3s;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.counter {
  font-size: 1.2rem;
  margin: 0 15px;
}

.success-modal,
.final-success {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3); /* 可改透明度，带点遮罩更清晰 */
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 20px;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 80%;
  max-width: 400px;
  text-align: center;
  z-index: 1001;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  padding: 20px;
  position: relative;
}

.modal-close {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 24px;
  cursor: pointer;
}


.file-upload-btn {
  display: inline-block;
  padding: 15px 30px;
  background: linear-gradient(45deg, #4ecdc4, #45b7b0);
  color: white;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin: 20px 0;
  border: 2px solid #fff;
}

.file-upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.hidden-input {
  display: none;
}

/* 调整弹窗位置 */
.top-modal {
  position: relative;
  top: 20vh; /* 距离顶部20%视口高度 */
  transform: none; /* 移除居中定位 */
  margin-top: 20px;
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from {
    top: -50px;
    opacity: 0;
  }
  to {
    top: 0;
    opacity: 1;
  }
}
.selected {
  outline: 3px solid #ff6b6b;
}

</style>
